import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
  ViewChild,
  type WritableSignal,
} from '@angular/core';
import { Commands, Resources, Search } from '@local/client-contracts';
import { Constants } from '@local/common';
import { isEmbed, isExtension } from '@local/common-web';
import { KeyName } from '@local/ts-infra';
import { PopupRef } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem } from '@shared/components';
import { ContextMenuService } from '@shared/components/context-menu/context-menu.service';
import { RouterService } from '@shared/services/router.service';
import { copyToClipboard } from '@shared/utils';
import { stopEvent } from '@shared/utils/elements-util';
import { OverlayPanel } from 'primeng/overlaypanel';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { NEW_CARD_URL_PARAM, PreviewService } from 'src/app/bar/services/preview.service';
import { WikiCardsService } from 'src/app/bar/services/wikis/wiki-cards.service';
import { lookUpQuesionTitle } from 'src/app/bar/utils/formatting-utils';
import { WikiCardStorageModel } from '../../../collections-page/models/wiki-card-model';
import { WikiItemSelectionPopupService } from '../../../collections-page/services/wiki-item-selection-popup.service';
import { EMBED_SAVE_CARD_KEY_STORAGE } from '../../../preview/helpers/preview.utils';
import { ResultContextMenuItem } from '../../models/results-types';
import { isWikiCard } from '../../utils/results.util';
import { AnswerActionsModel } from './answer-actions-model';
import { Tooltip } from 'primeng/tooltip';

@UntilDestroy()
@Component({
  selector: 'answer-actions',
  templateUrl: './answer-actions.component.html',
  styleUrls: ['./answer-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnswerActionsComponent {
  private readonly EVENT_X = 40;
  private readonly isEmbed = isEmbed();
  private _model: AnswerActionsModel;
  private textToCopy: string;
  actionActive: boolean;
  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;
  isShowFeedback: boolean;
  feedbackTypeSelected: Resources.FeedbackType;
  linkIds: string;
  resourceIds: string;
  isExtension = isExtension();
  toggleCopyTooltip: WritableSignal<boolean> = signal(false);
  get model(): AnswerActionsModel {
    return this._model;
  }
  get DISABLED_WIKIS_WORKSPACE_FEATURE_FLAG() {
    return Constants.DISABLED_WIKIS_WORKSPACE_FEATURE_FLAG;
  }

  @ViewChild(OverlayPanel) overlayFeedback: OverlayPanel;
  @ViewChild(Tooltip) tooltip;
  @Input() set model(value: AnswerActionsModel) {
    this._model = value;
    this.initData();
  }
  @Input() active: boolean;
  @Input() searchId: string;
  @Input() displayFeedbackAction = true;
  @Input() set isFeedbackOpen(val: boolean) {
    if (val) {
      this.actionActive = true;
    } else {
      this.onHideFeedback();
    }
  }

  @Output() feedbackShow = new EventEmitter<Resources.FeedbackType>();
  @Output() actionClosed = new EventEmitter<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private commandsService: CommandsService,
    private contextMenuService: ContextMenuService,
    private previewService: PreviewService,
    private routerService: RouterService,
    private wikiCardsService: WikiCardsService,
    private wikiItemSelectionPopupService: WikiItemSelectionPopupService
  ) {}

  private initData() {
    this.linkIds = this.model?.resources
      ?.map((r) => r.link?.id)
      .filter((linkId) => linkId)
      .join(',');
    this.resourceIds = this.model?.resources?.map((r) => r.id).join(',');
    this.textToCopy = this.buildCopyText();
  }

  async onCopyContent(): Promise<void> {
    await copyToClipboard(this.textToCopy);
    this.toggleCopyTooltip.update((value) => !value);
  }
  onCreateCard() {
    const data: WikiCardStorageModel = {
      title: this.model?.questionQuery,
      content: this.buildCardContent(),
      collectionId: this.model?.collectionId,
    };
    if (this.routerService.active === 'assistant-incontext' && this.isEmbed) {
      const id = EMBED_SAVE_CARD_KEY_STORAGE;
      localStorage.setItem(id, JSON.stringify(data));
      this.previewService.setPreviewState(
        'popup',
        1,
        {
          type: 'result',
          filterType: 'wiki-local',
          id,
          action: { type: 'wiki card' },
          source: 'wiki-drafts',
        },
        { [NEW_CARD_URL_PARAM]: true }
      );
    } else {
      this.wikiItemSelectionPopupService.openWikiItemSelectionPopup('create-card', null, { item: data });
    }
  }
  onShowFeedback(event, type: Resources.FeedbackType) {
    if (this.feedbackTypeSelected === type) {
      return;
    }
    stopEvent(event);
    this.actionActive = true;
    this.isShowFeedback = true;
    this.feedbackTypeSelected = type;
    this.feedbackShow.emit(type);
  }

  onHideFeedback() {
    this.isShowFeedback = false;
    this.actionActive = false;
    this.actionClosed.emit();
    this.cdr.markForCheck();
  }

  openContextMenu(event: MouseEvent, selectedText: string) {
    this.actionActive = true;
    const items = this.buildContextMenuItems(selectedText);
    const openAt: { x: number; y: number } = {
      x: event.x - this.EVENT_X,
      y: event?.y,
    };
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ResultContextMenuItem) => this.handleContextMenuCommand(item),
      },
      { position: 'below' }
    );
    this.activeContextMenu.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      this.actionActive = false;
      this.actionClosed.emit();
      this.cdr.markForCheck();
    });
  }

  private buildContextMenuItems(selectedText: string): ContextMenuItem[] {
    const items: ContextMenuItem[] = [];
    if (selectedText) {
      return [
        {
          id: 'copy',
          text: 'Copy',
          command: { type: 'copy-clipboard', value: selectedText } as Commands.CopyClipboard,
          shortcut: ['CommandOrControl', 'c'] as KeyName[],
          icon: { type: 'font-icon', value: 'icon-copy' },
        },
      ];
    }
    return items;
  }

  private async handleContextMenuCommand(context: ResultContextMenuItem) {
    switch (context.id) {
      case 'copy': {
        await this.commandsService.executeCommand(context.command);
        break;
      }
      case 'copy-answer': {
        this.onCopyContent();
        break;
      }
      case 'create-card': {
        this.onCreateCard();
        break;
      }
    }
  }

  private getResourceLink(resource: Search.ResultResourceItem) {
    return isWikiCard(resource)
      ? this.wikiCardsService.getCardUrl(resource?.resource?.data?.title, resource.resource?.data?.id, true)
      : (<Commands.OpenUrl>resource.view?.title?.onClick)?.url;
  }

  private buildCardContent(): string {
    const resourceArea = this.model?.resources
      ?.map(
        (resource) =>
          `<p class="frame-editor-link"><img alt="resource icon" src="${resource.view?.icon?.['lightUrl']}" style="width: 24px; height: 24px!important;"><a href="${this.getResourceLink(resource)}">${resource.view?.title?.text}</a></p>`
      )
      ?.join('');
    const answerArea =
      this.model?.answerIntent === 'ResourceLookup' ? `<p>${lookUpQuesionTitle}</p>` : `<p>${this.model?.formattedAnswer}</p>`;
    return `
        ${answerArea}${resourceArea || ''}<p>&nbsp;</p>`;
  }

  private buildCopyText() {
    if (this.model?.answerIntent != 'ResourceLookup') {
      return this.model?.answer;
    }
    return this.buildTextResourceLookupFromJson(this.model);
  }

  private buildTextResourceLookupFromJson(model: AnswerActionsModel) {
    if (!Array.isArray(model?.resources)) {
      return '';
    }
    return `${lookUpQuesionTitle}
    ${model.resources
      .map((resource: Search.ResultResourceItem) => {
        const title = resource.view?.title?.text || '';
        const link = this.getResourceLink(resource) || '';
        return `[${title}](${link})`;
      })
      .join('\n')}`;
  }
}
